import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendUp, GoogleLogo } from "@phosphor-icons/react";
import VerdictBadge from "./VerdictBadge";
import TrustBadge from "./TrustBadge";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_TRENDING = [
  {
    id: "sample-1",
    thumbnail: "https://images.unsplash.com/photo-1722392493604-7382c3d0f646?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwb2xpdGljaWFuJTIwc3BlZWNoJTIwcG9kaXVtfGVufDB8fHx8MTc4NTQwMjA4N3ww&ixlib=rb-4.1.0&q=85",
    title: "Political address — economic policy claims",
    verdict: "PARTIALLY_TRUE",
    view_count: 8412,
  },
  {
    id: "sample-2",
    thumbnail: "https://images.unsplash.com/photo-1742805382149-3c2f0cd0f300?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxuZXdzJTIwYW5jaG9yJTIwYnJvYWRjYXN0fGVufDB8fHx8MTc4NTQwMjA4N3ww&ixlib=rb-4.1.0&q=85",
    title: "Broadcast segment — health study claim",
    verdict: "FALSE",
    view_count: 5321,
  },
  {
    id: "sample-3",
    thumbnail: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxwb2xpdGljaWFuJTIwaW50ZXJ2aWV3JTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODU0MDIwODd8MA&ixlib=rb-4.1.0&q=85",
    title: "Podcast excerpt — historical fact discussion",
    verdict: "TRUE",
    view_count: 3110,
  },
];

export default function TrendingChecks({ onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, signIn } = useAuth();

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/v1/trending`)
      .then((r) => {
        if (!mounted) return;
        const real = (r.data?.items || [])
          .filter((d) => d.video_metadata && d.overall_verdict)
          .slice(0, 6)
          .map((d) => ({
            id: d.cache_key,
            thumbnail: d.video_metadata?.thumbnail_url,
            title: d.video_metadata?.title || "Untitled",
            verdict: d.overall_verdict,
            trust_score: d.trust_score,
            view_count: d.view_count || 1,
            video_url: d.video_url,
          }));
        setItems(real.length ? real : FALLBACK_TRENDING);
      })
      .catch(() => setItems(FALLBACK_TRENDING))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  return (
    <section data-testid="trending-checks" className="mt-16">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-1">
            LIVE FROM THE NEWSROOM
          </div>
          <h3 className="font-display text-3xl md:text-4xl text-zinc-950 leading-tight tracking-tight">
            Trending fact-checks
          </h3>
        </div>
        <TrendUp weight="duotone" className="w-6 h-6 text-zinc-900" />
      </div>

      {!user && (
        <div
          data-testid="trending-signin-cta"
          className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 border border-emerald-500 bg-emerald-50 px-4 py-3"
        >
          <div>
            <div className="font-mono-plex text-[10px] font-semibold text-emerald-800 mb-1">
              GET UNLIMITED FACT-CHECKS
            </div>
            <p className="text-[13px] text-emerald-900 leading-snug">
              Sign in with Google to save these to your account and keep fact-checking without the 3-per-browser limit.
            </p>
          </div>
          <button
            type="button"
            onClick={signIn}
            data-testid="trending-signin-btn"
            className="self-start md:self-auto inline-flex items-center gap-2 bg-emerald-600 text-white px-3 py-2 font-mono-plex text-[10px] font-semibold hover:bg-emerald-700 transition-colors"
          >
            <GoogleLogo weight="bold" className="w-3.5 h-3.5" />
            SIGN IN WITH GOOGLE
          </button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-zinc-200 bg-white aspect-[4/3] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.slice(0, 6).map((it, idx) => (
            <motion.button
              key={it.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => it.video_url && onSelect?.(it.video_url)}
              data-testid={`trending-item-${idx}`}
              className="group text-left border border-zinc-300 bg-white overflow-hidden hover-brutal"
            >
              <div className="relative aspect-video bg-zinc-100 overflow-hidden">
                {it.thumbnail && (
                  <img src={it.thumbnail} alt={it.title} className="w-full h-full object-cover" />
                )}
                <div className="absolute top-3 left-3">
                  <VerdictBadge verdict={it.verdict} size="sm" />
                </div>
                {typeof it.trust_score === "number" && (
                  <div className="absolute top-3 right-3">
                    <TrustBadge score={it.trust_score} size="sm" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-display text-lg text-zinc-950 leading-tight line-clamp-2 mb-3">
                  {it.title}
                </h4>
                <div className="flex items-center justify-between font-mono-plex text-[10px] text-zinc-500 font-semibold">
                  <span>{it.view_count.toLocaleString()} CHECKS</span>
                  <ArrowUpRight weight="bold" className="w-3.5 h-3.5 group-hover:text-zinc-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </section>
  );
}
